import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { useChildren, useCurrencies, useExponentByCode } from '../api/queries'
import { formatAmountOnly, formatMoney, parseMoneyInput } from '../lib/money'
import { kidAccent } from '../lib/kidAccent'

export function ChildLedgerPage() {
  const { id } = useParams<{ id: string }>()
  const childId = Number(id)
  const validChildId = Number.isFinite(childId)
  const queryClient = useQueryClient()
  const exponentByCode = useExponentByCode()
  const { data: children, isLoading: childrenLoading } = useChildren()
  const child = children?.find((c) => c.id === childId)

  const balanceQuery = useQuery({
    queryKey: ['balance', childId],
    enabled: validChildId,
    queryFn: async () => {
      const { data, error } = await api.GET('/children/{id}/balance', { params: { path: { id: childId } } })
      if (error) throw error
      return data
    },
  })

  const transactionsQuery = useQuery({
    queryKey: ['transactions', childId],
    enabled: validChildId,
    queryFn: async () => {
      // includeVoided: true so a voided transaction stays visible (struck through, via the
      // .voided row class below) instead of silently disappearing from the history - the
      // parent should be able to see that a reversal happened, not just that it did.
      const { data, error } = await api.GET('/children/{id}/transactions', {
        params: { path: { id: childId }, query: { limit: 100, includeVoided: true } },
      })
      if (error) throw error
      return data
    },
  })

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ['balance', childId] })
    queryClient.invalidateQueries({ queryKey: ['transactions', childId] })
    queryClient.invalidateQueries({ queryKey: ['summary'] })
  }

  const voidMutation = useMutation({
    mutationFn: async (transactionId: number) => {
      const { error } = await api.DELETE('/transactions/{id}', { params: { path: { id: transactionId } } })
      if (error) throw error
    },
    onSuccess: invalidateAll,
  })

  if (!validChildId || (!childrenLoading && !child)) {
    return <p className="loading">No such child.</p>
  }
  if (!child || balanceQuery.isLoading || transactionsQuery.isLoading) {
    return <p className="loading">Loading…</p>
  }

  return (
    <div className="child-ledger">
      <h1>
        <span className="kid-dot" style={{ backgroundColor: kidAccent(childId) }} />
        {child.displayName}
      </h1>
      {balanceQuery.data && (
        <p className={`balance-amount large${balanceQuery.data.balance.minorUnits < 0 ? ' negative' : ''}`}>
          {formatMoney(balanceQuery.data.balance, exponentByCode)}
        </p>
      )}

      <AddTransactionForm childId={childId} onAdded={invalidateAll} />

      <table className="transactions">
        <thead>
          <tr>
            <th>Date</th>
            <th className="num">Amount</th>
            <th className="num">USD</th>
            <th>Memo</th>
            <th>Source</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(transactionsQuery.data?.items ?? []).map((txn) => (
            <tr key={txn.id} className={txn.voided ? 'voided' : ''}>
              <td>{txn.occurredOn}</td>
              <td className="num">
                {txn.amount.currency !== 'USD' && <span className="stamp">{txn.amount.currency}</span>}
                <span className={`money${txn.amount.minorUnits < 0 ? ' negative' : ''}`}>
                  {txn.amount.currency === 'USD'
                    ? formatMoney(txn.amount, exponentByCode)
                    : formatAmountOnly(txn.amount, exponentByCode)}
                </span>
              </td>
              <td className="num">
                <span className={`money${txn.usdAmount.minorUnits < 0 ? ' negative' : ''}`}>
                  {formatMoney(txn.usdAmount, exponentByCode)}
                </span>
              </td>
              <td>{txn.memo ?? ''}</td>
              <td>{txn.source}</td>
              <td>
                {!txn.voided && (
                  <button type="button" onClick={() => voidMutation.mutate(txn.id)} disabled={voidMutation.isPending}>
                    Void
                  </button>
                )}
                {txn.voided && <span className="voided-label">voided</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AddTransactionForm({ childId, onAdded }: { childId: number; onAdded: () => void }) {
  const { data: currencies } = useCurrencies()
  const exponentByCode = useExponentByCode()
  const today = new Date().toISOString().slice(0, 10)

  const [occurredOn, setOccurredOn] = useState(today)
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [memo, setMemo] = useState('')
  const [error, setError] = useState<string | null>(null)

  const addMutation = useMutation({
    mutationFn: async () => {
      const exponent = exponentByCode[currency] ?? 2
      const amountMinorUnits = parseMoneyInput(amount, exponent)
      if (amountMinorUnits === null) throw new Error(`"${amount}" isn't a valid amount for ${currency}`)
      const { error: apiError } = await api.POST('/transactions', {
        body: { childId, occurredOn, amountMinorUnits, currency, memo: memo || null },
      })
      if (apiError) throw new Error(apiError.detail ?? apiError.title)
    },
    onSuccess: () => {
      setAmount('')
      setMemo('')
      setError(null)
      onAdded()
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <form
      className="add-transaction"
      onSubmit={(e) => {
        e.preventDefault()
        addMutation.mutate()
      }}
    >
      <input type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} required />
      <input
        type="text"
        inputMode="decimal"
        placeholder="Amount (negative to take back)"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        required
      />
      <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
        {(currencies ?? []).map((c) => (
          <option key={c.code} value={c.code}>
            {c.code}
          </option>
        ))}
      </select>
      <input type="text" placeholder="Memo" value={memo} onChange={(e) => setMemo(e.target.value)} />
      <button type="submit" disabled={addMutation.isPending}>
        Add
      </button>
      {error && <p className="error">{error}</p>}
    </form>
  )
}
